import { LayoutWithCarousel } from '@/components/Layout';
import Container, { Column } from '@/components/container/Container';
import Spacing from '@/components/container/Spacing';
import { TextBodyMedium, TextHeadlineLarge } from '@/components/text/Texts';
import LoadingDialog from '@/components/utils/LoadingDialog';
import { Card } from 'primereact/card';
import { useState } from 'react';
import '../../authentication-screens.styles.css';
import LoginLink from '../../components/LoginLink';
import { axiosInstance } from '@/services/axios.service';
import { BaseResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import { useForm } from 'react-hook-form';
import { RoundedFilledButton } from '@/components/buttons/Buttons';
import { object, string } from 'yup';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { MdOutlineCancel } from 'react-icons/md';
import { cn } from '@/components/ui/cn';
import { InputController } from '@/components/inputs/InputController';
import TextInput from '@/components/inputs/TextInput';

const schema = object({
	Email: string()
		.trim()
		.required(VALIDATION_MESSAGES.required)
		.email('Invalid email format')
});

interface SendPasswordResetEmailRequest {
	Email: string;
}

const ForgotPasswordScreen = () => {
	const [sentEmail, setSentEmail] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const form = useForm<SendPasswordResetEmailRequest>({
		resolver: yupResolver(schema),
		mode: 'all'
	});

	const submitButtonIsActive = form.formState.isValid;

	const handleClick = async () => {
		setIsLoading(true);
		try {
			const response = await axiosInstance.post<BaseResponse>(ENDPOINTS['/api/X4bUser/SendPasswordResetEmail'], { Email: form.getValues().Email } as SendPasswordResetEmailRequest);
			const testLink = (response.data as any)?.Data["Link"];
			if (testLink) {
				console.log(`Reset password test link: ${testLink}`);
			}
			setTimeout(() => {
				setSentEmail(!sentEmail);
				setIsLoading(false);
			}, 0);
		} finally {
			setIsLoading(false);
		}
	};
	return (
		<>
			<LoadingDialog isLoading={isLoading} />
			<LayoutWithCarousel>
				<Card className="login-screen !font-poppins">
					<LoginLink />

					<Spacing height="h-6" />
					{!sentEmail ? (
						<>
							<TextHeadlineLarge
								text="Forgot my Password"
								className="text-inverseSurface"
							/>
							<Spacing height="h-1" />
							<TextBodyMedium
								text="Enter your email address to reset you password"
								className="text-formTextGray"
							/>
							<Spacing height="h-8" />

							<Column className="gap-6">
								<InputController id="Email" control={form.control}>
									<TextInput
										label="Email"
										placeholder="Enter your email"
										iconCls={cn(!form.watch('Email') && 'hidden')}
										icon={(
											<MdOutlineCancel
												size={24}
												className="text-primary hover:cursor-pointer"
												onClick={() => form.setValue('Email', '', { shouldValidate: true })}
											/>
										)}
									/>
								</InputController>
								<RoundedFilledButton
									className="login-button"
									label="Submit"
									onClick={handleClick}
									disabled={!submitButtonIsActive}
									id="forgot-password"
								/>
							</Column>
						</>
					) : (
						<Container>
							<TextHeadlineLarge
								text="We sent you an e-mail"
								className="text-inverseSurface"
							/>
							<Spacing height="h-1" />
							<TextBodyMedium
								text="We’ve sent a link to your registered email address. Click on it to proceed with resetting your password."
								className="text-formTextGray"
							/>
						</Container>
					)}
				</Card>
			</LayoutWithCarousel>
		</>
	);
};

export default ForgotPasswordScreen;
